import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  Favorite as FavoriteIcon,
  Lightbulb as LightbulbIcon,
  CheckCircle as CheckCircleIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { frCA, enUS } from 'date-fns/locale';
import { useCreatePost, useCreatePostReaction, useDeletePost, useDeletePostReaction } from 'services/Data/PostServices';
import { useCreateReply, useCreateReplyReaction, useDeleteReply, useDeleteReplyReaction } from 'services/Data/ReplyServices';
import ReactionTypeEnumValue, { getReactionConfig } from 'enums/ReactionTypeEnum';
import { generateGUID } from 'services/Utilities/guidUtils';

// Social feed with posts, replies, and reactions (mirrors certification tool)
const SocialFeed = ({ submissionId, submissionDetailId, posts = [], isLoading = false, isReadOnly = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const accountReducer = useSelector(state => state.AccountReducer);

  const [newPostContent, setNewPostContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  const createPostMutation = useCreatePost(submissionId);
  const createReplyMutation = useCreateReply(submissionId);
  const createPostReactionMutation = useCreatePostReaction(submissionId);
  const createReplyReactionMutation = useCreateReplyReaction(submissionId);
  const deletePostMutation = useDeletePost(submissionId);
  const deleteReplyMutation = useDeleteReply(submissionId);
  const deletePostReactionMutation = useDeletePostReaction(submissionId);
  const deleteReplyReactionMutation = useDeleteReplyReaction(submissionId);

  const currentUser = accountReducer.profile;

  const handleCreatePost = async () => {
    if (isReadOnly || !newPostContent.trim()) return;

    const postData = {
      id: generateGUID(),
      content: newPostContent,
      user_Id: currentUser?.id,
    };

    if (submissionDetailId) {
      postData.submissionDetail_Id = submissionDetailId;
    } else if (submissionId) {
      postData.submission_Id = submissionId;
    }

    await createPostMutation.mutateAsync(postData);
    setNewPostContent('');
  };

  const handleCreateReply = async postId => {
    if (isReadOnly || !replyContent.trim()) return;

    await createReplyMutation.mutateAsync({
      id: generateGUID(),
      content: replyContent,
      post_Id: postId,
      user_Id: currentUser?.id,
    });

    setReplyContent('');
    setReplyingTo(null);
  };

  const handleReaction = async (type, targetId, targetType = 'post') => {
    if (isReadOnly) return;

    const target =
      targetType === 'post'
        ? posts.find(p => p.id === targetId)
        : posts.flatMap(p => p.replies || []).find(r => r.id === targetId);

    if (!target) return;

    const existingReaction =
      targetType === 'post'
        ? target.postReactions?.find(r => r.user_Id === currentUser?.id && r.reactionType === type)
        : target.reactions?.find(r => r.user_Id === currentUser?.id && r.reactionType === type);

    if (existingReaction) {
      if (targetType === 'post') {
        await deletePostReactionMutation.mutateAsync(existingReaction.id);
      } else {
        await deleteReplyReactionMutation.mutateAsync(existingReaction.id);
      }
    } else {
      const reactionData = {
        id: generateGUID(),
        reactionType: type,
        user_Id: currentUser?.id,
      };

      if (targetType === 'post') {
        reactionData.post_Id = targetId;
        await createPostReactionMutation.mutateAsync(reactionData);
      } else {
        reactionData.reply_Id = targetId;
        await createReplyReactionMutation.mutateAsync(reactionData);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
          {t('common.loading')}...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2}>
        {posts.map(post => (
          <PostCard
            key={post.id}
            post={post}
            onReaction={handleReaction}
            onReply={postId => setReplyingTo(postId)}
            onCancelReply={() => {
              setReplyingTo(null);
              setReplyContent('');
            }}
            onDelete={postId => deletePostMutation.mutate(postId)}
            currentUser={currentUser}
            replyingTo={replyingTo}
            replyContent={replyContent}
            setReplyContent={setReplyContent}
            handleCreateReply={handleCreateReply}
            deleteReplyMutation={deleteReplyMutation}
            theme={theme}
            isReadOnly={isReadOnly}
          />
        ))}
      </Stack>

      {posts.length === 0 && !isReadOnly && (
        <Card sx={{ bgcolor: theme.palette.background.paper, mt: 2, border: `1px solid ${theme.palette.divider}` }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: '#FFD200',
                  color: '#1a1a1a',
                  width: 40,
                  height: 40,
                  fontWeight: 700,
                }}>
                {currentUser?.userName?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  placeholder={t('social.writeComment')}
                  value={newPostContent}
                  onChange={e => setNewPostContent(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: theme.palette.text.primary,
                      bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#F5F5F5',
                      fontSize: '0.875rem',
                      '& fieldset': { borderColor: theme.palette.divider },
                      '&:hover fieldset': { borderColor: '#FFD200' },
                      '&.Mui-focused fieldset': { borderColor: '#FFD200' },
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!newPostContent.trim() || createPostMutation.isPending}
                    onClick={handleCreatePost}
                    endIcon={<SendIcon />}
                    sx={{
                      bgcolor: '#FFD200',
                      color: '#1a1a1a',
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#FFC700' },
                      '&.Mui-disabled': { bgcolor: 'rgba(255, 210, 0, 0.3)' },
                    }}>
                    <Typography variant="h3">{t('social.post')}</Typography>
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const PostCard = ({
  post,
  onReaction,
  onReply,
  onCancelReply,
  onDelete,
  currentUser,
  replyingTo,
  replyContent,
  setReplyContent,
  handleCreateReply,
  deleteReplyMutation,
  theme,
  isReadOnly,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [anchorEl, setAnchorEl] = useState(null);

  const isAuthor = currentUser?.id === post.user_Id;
  const reactionCounts = getReactionCounts(post.postReactions);
  const reactionsByType = getReactionsByType(post.postReactions);
  const sortedReplies = (post.replies || []).slice().sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));

  const hasLikeReaction = post.postReactions?.some(
    r => r.user_Id === currentUser?.id && r.reactionType === ReactionTypeEnumValue.LIKE,
  );
  const hasLoveReaction = post.postReactions?.some(
    r => r.user_Id === currentUser?.id && r.reactionType === ReactionTypeEnumValue.LOVE,
  );
  const hasHelpfulReaction = post.postReactions?.some(
    r => r.user_Id === currentUser?.id && r.reactionType === ReactionTypeEnumValue.HELPFUL,
  );
  const hasResolvedReaction = post.postReactions?.some(
    r => r.user_Id === currentUser?.id && r.reactionType === ReactionTypeEnumValue.RESOLVED,
  );

  const getLocale = lang => {
    switch (lang) {
      case 'fr':
        return frCA;
      case 'en':
      default:
        return enUS;
    }
  };

  return (
    <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: '#FFD200',
                color: '#1a1a1a',
                width: 40,
                height: 40,
                fontWeight: 700,
              }}>
              {post.userName?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                {post.userName || t('social.anonymous')}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {formatDistanceToNow(new Date(post.dateCreated), { addSuffix: true, locale: getLocale(currentLanguage) })}
              </Typography>
            </Box>
          </Box>
          {isAuthor && !isReadOnly && (
            <IconButton
              size="small"
              onClick={e => setAnchorEl(e.currentTarget)}
              sx={{ color: theme.palette.text.secondary }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem
              onClick={() => {
                if (!isReadOnly) onDelete(post.id);
                setAnchorEl(null);
              }}>
              {t('common.delete')}
            </MenuItem>
          </Menu>
        </Box>

        <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 2, whiteSpace: 'pre-wrap' }}>
          {post.content}
        </Typography>

        {Object.keys(reactionCounts).length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            {Object.entries(reactionCounts).map(([type, count]) => {
              const config = getReactionConfig(parseInt(type, 10));
              const reactions = reactionsByType[type] || [];
              const tooltipTitle = (
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {reactions.map((reaction, idx) => (
                    <Box key={idx} sx={{ py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {reaction.userName || t('social.anonymous')}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, fontSize: '0.7rem' }}>
                        {formatDistanceToNow(new Date(reaction.dateCreated), {
                          addSuffix: true,
                          locale: getLocale(currentLanguage),
                        })}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
              return (
                <Tooltip key={type} title={tooltipTitle} arrow placement="top">
                  <Chip
                    size="small"
                    label={`${config.icon} ${count}`}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      color: config.color,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}

        <Divider sx={{ borderColor: theme.palette.divider, my: 1 }} />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <ReactionButton
            icon={<ThumbUpIcon fontSize="small" />}
            label={t('social.like')}
            active={hasLikeReaction}
            onClick={() => onReaction(ReactionTypeEnumValue.LIKE, post.id, 'post')}
            iconColor="#4267B2"
            disabled={isReadOnly}
          />
          <ReactionButton
            icon={<FavoriteIcon fontSize="small" />}
            label={t('social.love')}
            active={hasLoveReaction}
            onClick={() => onReaction(ReactionTypeEnumValue.LOVE, post.id, 'post')}
            iconColor="#F02849"
            disabled={isReadOnly}
          />
          <ReactionButton
            icon={<LightbulbIcon fontSize="small" />}
            label={t('social.helpful')}
            active={hasHelpfulReaction}
            onClick={() => onReaction(ReactionTypeEnumValue.HELPFUL, post.id, 'post')}
            iconColor="#FFD200"
            disabled={isReadOnly}
          />
          <ReactionButton
            icon={<CheckCircleIcon fontSize="small" />}
            label={t('social.resolved')}
            active={hasResolvedReaction}
            onClick={() => onReaction(ReactionTypeEnumValue.RESOLVED, post.id, 'post')}
            iconColor="#4CAF50"
            disabled={isReadOnly}
          />
        </Box>
        <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid rgba(255, 210, 0, 0.2)' }}>
          <Stack spacing={1.5}>
            {sortedReplies.map(reply => (
              <ReplyCard
                key={reply.id}
                reply={reply}
                onReaction={onReaction}
                onDelete={replyId => {
                  if (!isReadOnly) {
                    deleteReplyMutation.mutate(replyId);
                  }
                }}
                currentUser={currentUser}
                theme={theme}
                isReadOnly={isReadOnly}
              />
            ))}

            {replyingTo === post.id && !isReadOnly && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={4}
                  placeholder={t('social.writeReply')}
                  value={replyContent}
                  onChange={e => setReplyContent(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#F5F5F5',
                      fontSize: '0.85rem',
                      '& fieldset': { borderColor: theme.palette.divider },
                      '&:hover fieldset': { borderColor: '#FFD200' },
                      '&.Mui-focused fieldset': { borderColor: '#FFD200' },
                    },
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleCreateReply(post.id)}
                  disabled={!replyContent.trim()}
                  sx={{
                    bgcolor: '#FFD200',
                    color: '#1a1a1a',
                    fontWeight: 700,
                    minWidth: 'auto',
                    px: 2,
                    '&:hover': { bgcolor: '#FFC700' },
                  }}>
                  <SendIcon fontSize="small" />
                </Button>
                <IconButton
                  size="small"
                  onClick={onCancelReply}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&:hover': { color: theme.palette.text.primary, bgcolor: 'rgba(0,0,0,0.04)' },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {replyingTo !== post.id && !isReadOnly && (
              <Button
                size="small"
                onClick={() => onReply(post.id)}
                sx={{
                  alignSelf: 'flex-start',
                  color: theme.palette.mode === 'dark' ? '#FFD200' : '#000000',
                  bgcolor: theme.palette.mode === 'dark' ? 'transparent' : '#FFD200',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 210, 0, 0.1)' : '#FFC700',
                  },
                }}>
                {t('social.addReply')}
              </Button>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

const ReplyCard = ({ reply, onReaction, onDelete, currentUser, theme, isReadOnly }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const isAuthor = currentUser?.id === reply.user_Id;

  const reactionCounts = getReactionCounts(reply.reactions);
  const reactionsByType = getReactionsByType(reply.reactions);

  const getLocale = lang => {
    switch (lang) {
      case 'fr':
        return frCA;
      case 'en':
      default:
        return enUS;
    }
  };

  return (
    <Card sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 1.5, pb: '0px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: '#FFD200',
                color: '#1a1a1a',
                width: 36,
                height: 36,
                fontWeight: 700,
              }}>
              {reply.userName?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, fontWeight: 600, lineHeight: 1 }}>
                {reply.userName || t('social.anonymous')}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {formatDistanceToNow(new Date(reply.dateCreated), { addSuffix: true, locale: getLocale(currentLanguage) })}
              </Typography>
            </Box>
          </Box>
          {isAuthor && !isReadOnly && (
            <IconButton size="small" onClick={() => onDelete(reply.id)} sx={{ color: theme.palette.text.secondary }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Typography variant="body2" sx={{ color: theme.palette.text.primary, mb: 2, ml: 0, mt: 4, whiteSpace: 'pre-wrap' }}>
          {reply.content}
        </Typography>

        <Divider sx={{ borderColor: theme.palette.divider, mt: 4, mb: 0 }} />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {[
            { type: ReactionTypeEnumValue.LIKE, icon: '👍' },
            { type: ReactionTypeEnumValue.LOVE, icon: '❤️' },
            { type: ReactionTypeEnumValue.HELPFUL, icon: '💡' },
            { type: ReactionTypeEnumValue.RESOLVED, icon: '✅' },
          ].map(({ type, icon }) => {
            const count = reactionCounts[type] || 0;
            const isActive = reply.reactions?.some(r => r.user_Id === currentUser?.id && r.reactionType === type);
            const reactions = reactionsByType[type] || [];
            const tooltipTitle =
              reactions.length > 0 ? (
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {reactions.map((reaction, idx) => (
                    <Box key={idx} sx={{ py: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {reaction.userName || t('social.anonymous')}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, fontSize: '0.7rem' }}>
                        {formatDistanceToNow(new Date(reaction.dateCreated), {
                          addSuffix: true,
                          locale: getLocale(currentLanguage),
                        })}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                ''
              );

            return (
              <Tooltip key={type} title={tooltipTitle} arrow placement="top">
                <span>
                  <Button
                    size="small"
                    onClick={() => onReaction(type, reply.id, 'reply')}
                    disabled={isReadOnly}
                    sx={{
                      minWidth: 'auto',
                      px: 0.75,
                      fontSize: '0.8rem',
                      color: isActive ? '#FFD200' : 'text.secondary',
                      '&:hover': { bgcolor: 'rgba(255, 210, 0, 0.1)' },
                    }}>
                    {icon} {count > 0 && count}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const ReactionButton = ({ icon, label, active, onClick, iconColor, disabled = false }) => {
  return (
    <Button
      size="small"
      onClick={onClick}
      disabled={disabled}
      startIcon={icon}
      sx={{
        textTransform: 'none',
        color: active ? iconColor : 'text.secondary',
        fontWeight: active ? 700 : 500,
        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
      }}>
      {label}
    </Button>
  );
};

const getReactionCounts = reactions => {
  if (!reactions || reactions.length === 0) return {};
  return reactions.reduce((acc, reaction) => {
    const { reactionType } = reaction;
    acc[reactionType] = (acc[reactionType] || 0) + 1;
    return acc;
  }, {});
};

const getReactionsByType = reactions => {
  if (!reactions || reactions.length === 0) return {};
  return reactions.reduce((acc, reaction) => {
    const { reactionType } = reaction;
    if (!acc[reactionType]) acc[reactionType] = [];
    acc[reactionType].push(reaction);
    return acc;
  }, {});
};

export default SocialFeed;
